// Выбор вида в разделе «Квоты».
//
// Выпадающий список не годился: подпись «Камбалы дальневосточные (451) —
// 75 740,8 т» растягивала его шире панели, и выбор уезжал за край. Теперь вид —
// своя строка во всю ширину, а список раскрывается В ТЕЛЕ панели (не поверх:
// панель обрезает выпадающее содержимое) с поиском по имени и коду и полосой
// тоннажа у каждого вида — по ней видно, чем год богат, ещё до выбора.

import { useEffect, useRef } from 'react'
import { useT } from '../i18n'
import { barShare, filterSpecies } from './speciesFilter'
import type { SpeciesItem } from './speciesFilter'

const focusRing = 'focus-visible:outline focus-visible:outline-2 focus-visible:outline-sonar'

/** Строка выбранного вида в шапке панели; в режиме выбора — поле поиска. */
export function SpeciesField({ species, value, open, query, onOpen, onClose, onQuery, onPick, tons }: {
  species: readonly SpeciesItem[]
  value: number | null
  open: boolean
  query: string
  onOpen: () => void
  onClose: () => void
  onQuery: (q: string) => void
  onPick: (code: number | null) => void
  tons: (v: number) => string
}) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement | null>(null)
  useEffect(() => { if (open) inputRef.current?.focus() }, [open])
  const current = species.find(s => s.code === value) ?? null

  if (open) {
    return (
      <div className="flex items-center gap-2 rounded-lg border border-sonar bg-deep px-2.5 py-1.5">
        <input ref={inputRef} type="text" value={query}
          aria-label={t('quotas.speciesSearch')} placeholder={t('quotas.speciesSearch')}
          className="min-w-0 flex-1 bg-transparent text-[13px] text-foam placeholder:text-mist focus:outline-none"
          onChange={e => onQuery(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Escape') onClose()
            if (e.key === 'Enter') {
              const first = filterSpecies(species, query)[0]
              if (first) onPick(first.code)
            }
            if (e.key === 'ArrowDown') {
              e.preventDefault()
              document.querySelector<HTMLButtonElement>('[data-species-option]')?.focus()
            }
          }} />
        <button type="button" onClick={onClose}
          className={`shrink-0 text-[12px] text-mist hover:text-foam ${focusRing}`}>
          {t('quotas.speciesDone')}
        </button>
      </div>
    )
  }

  return (
    <button type="button" onClick={onOpen} aria-haspopup="listbox"
      className={`group flex w-full items-baseline gap-2 rounded-lg border border-hull bg-deep px-2.5 py-1.5 text-left hover:border-mist ${focusRing}`}>
      <span className="min-w-0 flex-1 truncate text-[13px] text-foam">
        {current ? current.name : t('quotas.allSpecies')}
        {current && current.code > 0 && <span className="tnum ml-1.5 text-[11px] text-mist">{current.code}</span>}
      </span>
      {current && (
        <span className="tnum shrink-0 whitespace-nowrap text-[12px] text-mist">
          {tons(current.tons)} {t('quotas.t')}
        </span>
      )}
      <svg aria-hidden viewBox="0 0 12 12" className="h-3 w-3 shrink-0 self-center text-mist group-hover:text-foam">
        <path d="M2.5 4.5 6 8l3.5-3.5" fill="none" stroke="currentColor" strokeWidth="1.5" />
      </svg>
    </button>
  )
}

/** Список видов в теле панели: «Все виды» и виды года с полосой тоннажа. */
export function SpeciesList({ species, value, query, onPick, tons }: {
  species: readonly SpeciesItem[]
  value: number | null
  query: string
  onPick: (code: number | null) => void
  tons: (v: number) => string
}) {
  const t = useT()
  const shown = filterSpecies(species, query)
  const sum = species.reduce((a, s) => a + s.tons, 0)

  const row = (selected: boolean) =>
    `flex w-full flex-col gap-1 border-b border-hull border-l-2 px-3 py-2 text-left hover:bg-deep ${focusRing} `
    + (selected ? 'border-l-sonar bg-deep' : 'border-l-transparent')

  const move = (e: React.KeyboardEvent<HTMLButtonElement>) => {
    if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return
    e.preventDefault()
    const all = Array.from(document.querySelectorAll<HTMLButtonElement>('[data-species-option]'))
    const i = all.indexOf(e.currentTarget)
    all[e.key === 'ArrowDown' ? Math.min(all.length - 1, i + 1) : Math.max(0, i - 1)]?.focus()
  }

  return (
    <ul role="listbox" aria-label={t('quotas.species')}>
      {!query.trim() && (
        <li>
          <button type="button" role="option" aria-selected={value == null} data-species-option
            onClick={() => onPick(null)} onKeyDown={move} className={row(value == null)}>
            <span className="flex w-full items-baseline gap-2">
              <span className="flex-1 text-[13px] text-foam">{t('quotas.allSpecies')}</span>
              <span className="tnum whitespace-nowrap text-[12px] text-foam">{tons(sum)}</span>
            </span>
          </button>
        </li>
      )}
      {shown.map(s => (
        <li key={s.code}>
          <button type="button" role="option" aria-selected={s.code === value} data-species-option
            onClick={() => onPick(s.code)} onKeyDown={move} className={row(s.code === value)}>
            <span className="flex w-full items-baseline gap-2">
              <span className="min-w-0 flex-1 text-[13px] text-foam">
                {s.name}{s.code > 0 && <span className="tnum ml-1.5 text-[11px] text-mist">{s.code}</span>}
              </span>
              <span className="tnum whitespace-nowrap text-[12px] text-foam">{tons(s.tons)}</span>
            </span>
            <span aria-hidden className="block h-[3px] w-full rounded-full bg-hull">
              <span className="block h-full rounded-full bg-sonar"
                style={{ width: `${Math.max(1.5, barShare(s.tons, species) * 100)}%` }} />
            </span>
          </button>
        </li>
      ))}
      {shown.length === 0 && (
        <li className="px-3 py-3 text-[12px] text-mist">{t('quotas.speciesNoMatch', { query: query.trim() })}</li>
      )}
    </ul>
  )
}
